import { useMemo, useState, type ChangeEvent } from 'react';
import {
  Alert, Chip, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { useOpinie } from '@/lib/useAnalytics';
import type { OpinieZapytanie } from '@/lib/api/analytics';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * OPINIE — co uczniowie sądzą o daniach.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * SIEDEM KOLUMN, WSZYSTKIE FILTROWALNE, ŻADNA SORTOWALNA. Zmierzone ze starego panelu:
 * każdy nagłówek ma `isSearchable = true` i `isSortable = false`, a mimo to
 * `headers[0].isSortedDescending = true`. Znaczy to dokładnie tyle: domyślny porządek to data
 * malejąco, a nagłówki nie są klikalne.
 *
 * Dodanie sortowania byłoby ULEPSZENIEM, a zadaniem jest parytet. Ulepszenia mają własny tryb
 * i własną decyzję — wprowadzone mimochodem przestają być widoczne jako zmiana.
 *
 * ┌─ PODSUMOWANIE LICZY SIĘ PO FILTRACH ────────────────────────────────────────────────────────┐
 * │ Procent negatywnych ma odpowiadać tabeli, która jest pod nim. Liczony po całym zbiorze      │
 * │ pokazywałby liczbę, której nie da się w tabeli odnaleźć — a to jedyna liczba, na którą      │
 * │ się tu patrzy.                                                                              │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 */

function formatData(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pl-PL');
}

export function OpiniePage() {
  const [od, setOd] = useState('');
  const [doo, setDoo] = useState('');
  const [szkola, setSzkola] = useState('');
  const [danie, setDanie] = useState('');
  const [uczen, setUczen] = useState('');
  const [restauracja, setRestauracja] = useState('');
  const [rodzaj, setRodzaj] = useState('');
  const [komentarz, setKomentarz] = useState('');

  const zapytanie: OpinieZapytanie = useMemo(() => ({
    date_from: od || undefined,
    date_to: doo || undefined,
    szkola: szkola || undefined,
    danie: danie || undefined,
    uczen: uczen || undefined,
    restauracja: restauracja || undefined,
    rodzaj: rodzaj || undefined,
    komentarz: komentarz || undefined,
  }), [od, doo, szkola, danie, uczen, restauracja, rodzaj, komentarz]);

  const { data, isLoading, error } = useOpinie(zapytanie);
  const opinie = data?.opinie ?? [];
  const p = data?.podsumowanie;

  const zmien = (ustaw: (v: string) => void) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => ustaw(e.target.value);

  return (
    <Stack spacing={2}>
      <NaglowekEkranu
        tytul="Opinie"
        opis="Oceny uczniów wystawione daniom — z komentarzem, szkołą i restauracją."
      />

      {data?.zaleznosc && (
        <Alert severity="warning">
          <strong>Opinie nie są jeszcze replikowane.</strong> {data.zaleznosc.skutek}{' '}
          Brakuje: <code>{data.zaleznosc.czego_brak}</code> (strumień {data.zaleznosc.strumien}).
        </Alert>
      )}

      {/*
        Podsumowanie pokazujemy TYLKO gdy źródło jest podłączone. „0 negatywnych, 0%" przy
        nieistniejącej replice byłoby pochwałą, na którą nic nie wskazuje.
      */}
      {data?.zrodlo_podlaczone && p && (
        <Karta>
          <Stack direction="row" spacing={4} useFlexGap sx={{ p: 2, flexWrap: 'wrap' }}>
            {[
              ['Pozytywne', String(p.pozytywne), undefined],
              ['Negatywne', String(p.negatywne), undefined],
              ['Udział negatywnych', `${p.procent_negatywnych.toLocaleString('pl-PL')}%`,
                'liczone po bieżących filtrach'],
            ].map(([etykieta, wartosc, opis]) => (
              <Stack key={etykieta as string} spacing={0.25}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{etykieta}</Typography>
                <Typography variant="h5">{wartosc}</Typography>
                {opis && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{opis}</Typography>
                )}
              </Stack>
            ))}
          </Stack>
        </Karta>
      )}

      <Karta>
        <Stack direction="row" spacing={1} useFlexGap sx={{ p: 2, flexWrap: 'wrap' }}>
          <TextField label="Data od" type="date" size="small" value={od}
            onChange={zmien(setOd)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField label="Data do" type="date" size="small" value={doo}
            onChange={zmien(setDoo)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField label="Szkoła" size="small" value={szkola} onChange={zmien(setSzkola)} />
          <TextField label="Danie" size="small" value={danie} onChange={zmien(setDanie)} />
          <TextField label="Uczeń" size="small" value={uczen} onChange={zmien(setUczen)} />
          <TextField label="Restauracja" size="small" value={restauracja} onChange={zmien(setRestauracja)} />
          <TextField label="Rodzaj opinii" size="small" select sx={{ minWidth: 160 }}
            value={rodzaj} onChange={zmien(setRodzaj)}>
            <MenuItem value="">wszystkie</MenuItem>
            <MenuItem value="Pozytywna">Pozytywna</MenuItem>
            <MenuItem value="Negatywna">Negatywna</MenuItem>
          </TextField>
          <TextField label="Komentarz" size="small" value={komentarz} onChange={zmien(setKomentarz)} />
        </Stack>
      </Karta>

      {isLoading && <LoadingState />}
      {error ? <ErrorState error={error} context="Ekran Opinii." /> : null}

      {!isLoading && !error && opinie.length === 0 && (
        <EmptyState
          title={data?.zrodlo_podlaczone ? 'Brak opinii w tym zakresie' : 'Źródło nie jest podłączone'}
          description={data?.zrodlo_podlaczone
            ? 'Zmień filtry, żeby zobaczyć inne opinie.'
            : 'Pusta lista NIE znaczy tu, że nikt nie narzeka — znaczy, że Control Center jeszcze tych opinii nie widzi.'}
        />
      )}

      {opinie.length > 0 && (
        <Karta>
          <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Opinie uczniów">
              <TableHead>
                <TableRow>
                  {['Data', 'Szkoła', 'Danie', 'Uczeń', 'Restauracja', 'Rodzaj opinii', 'Komentarz'].map((k) => (
                    // Nagłówki NIE są klikalne — w starym panelu żadna kolumna nie jest sortowalna.
                    <TableCell key={k} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{k}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {opinie.map((o, i) => (
                  <TableRow key={`${o.data}-${o.uczen}-${i}`} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatData(o.data)}</TableCell>
                    <TableCell>{o.szkola ?? '—'}</TableCell>
                    <TableCell>{o.danie ?? '—'}</TableCell>
                    <TableCell>{o.uczen ?? '—'}</TableCell>
                    <TableCell>{o.restauracja ?? '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={o.rodzaj}
                        color={o.rodzaj === 'Negatywna' ? 'error' : 'success'}
                        variant={o.rodzaj === 'Negatywna' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 420 }}>
                      <Typography variant="body2">{o.komentarz ?? '—'}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Karta>
      )}
    </Stack>
  );
}

export default OpiniePage;
